import {getTestProps} from '@benchling/js-test-utils';
import reactUpdate from 'immutability-helper';
import $ from 'jquery';
import React from 'react';
import ReactDOM from 'react-dom/client';
import {debounce} from 'ts-debounce';
import _ from 'underscore';

import {assert} from 'Asserts';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import setTestAttrs from 'test/lib/cypress/setTestAttrs';
import uniqueId from 'util/uniqueId';

const REACT_KEY_PREFIX = 'modal';

export interface Modal {
  key: unknown;
  reactKey: string;
  modal?: React.ReactNode;
}

let $container: JQuery | null = null;

// {key, modal, reactKey} ordered back-to-front.
let orderedKeyModals: Array<Modal> = [];

interface ModalContainerProps {
  orderedKeyModals: Array<Modal>;
}

/**
 * @deprecated ⚠️ Use `ModalsProvider` with `@mantine/modals-manager` or `runModal` instead for imperative
 * modal creation.
 */
// eslint-disable-next-line react/prefer-stateless-function
export class LegacyModalContainer extends React.Component<ModalContainerProps> {
  override render(): JSX.Element | null {
    if (this.props.orderedKeyModals.length === 0) {
      return null;
    }
    return (
      <div {...getTestProps({component: 'LegacyModalContainer', element: 'wrapper'})}>
        {this.props.orderedKeyModals.map(({modal, reactKey}, index) => (
          <div
            key={reactKey}
            className={index !== this.props.orderedKeyModals.length - 1 ? 'covered-modal' : undefined}
          >
            {modal}
          </div>
        ))}
      </div>
    );
  }
}

let legacyModalContainerReactRoot: ReactDOM.Root | null = null;

// Debounce so that ReactDOM.createRoot isn't called twice in the same call stack.
const productionRender = debounce(() => {
  assert($container != null, "Modal container's DOM node is not defined");
  legacyModalContainerReactRoot = renderToDOM(
    <LegacyModalContainer orderedKeyModals={orderedKeyModals} />,
    $container[0],
    legacyModalContainerReactRoot
  );
}, 1);

let render: (() => void) & {cancel: () => void} = productionRender;

export function overrideRenderForRtlTests(
  renderWithOrderedKeyModals: (orderedKeyModals: Array<Modal>) => Promise<void>
): void {
  render.cancel();
  // Debounce to mimic productionRender. This uses a plain timer because cancelling a ts-debounce function
  // rejects its pending calls, which open() and close() never await.
  let pendingRender: ReturnType<typeof setTimeout> | undefined;
  render = Object.assign(
    () => {
      clearTimeout(pendingRender);
      pendingRender = setTimeout(() => {
        pendingRender = undefined;
        renderWithOrderedKeyModals(orderedKeyModals);
      }, 0);
    },
    {
      cancel: () => {
        clearTimeout(pendingRender);
        pendingRender = undefined;
      },
    }
  );
}

export function restoreForTests(): void {
  render.cancel();
  render = productionRender;
  orderedKeyModals = [];
}

export function init(): void {
  $container = $('<div>').addClass('modal-container');
  setTestAttrs($container, {component: 'LegacyModalContainer', element: 'root'});
  $('body').append($container);
}

export function open(key: unknown, modal: React.ReactNode): Modal | null {
  let wasReplaced = false;
  orderedKeyModals = orderedKeyModals.map((km) => {
    if (km.key !== key) {
      return km;
    }
    wasReplaced = true;
    return reactUpdate(km, {$merge: {modal}});
  });
  if (!wasReplaced) {
    // eslint-disable-next-line no-restricted-syntax
    const reactKey = _.isString(key) ? key : uniqueId(REACT_KEY_PREFIX);
    const keyModal = {key, modal, reactKey};
    orderedKeyModals = orderedKeyModals.concat([keyModal]);
  }
  render();
  return null;
}

export function close(key: unknown): void {
  const keyFilter = (km: Modal): boolean => km.key !== key;
  if (orderedKeyModals.every((km) => keyFilter(km))) {
    return;
  }
  orderedKeyModals = orderedKeyModals.filter((km) => keyFilter(km));
  render();
}

export function getModalsByKeyPrefixString(keyPrefix: string): Array<React.ReactNode> {
  return (
    orderedKeyModals
      ?.filter((modal) => (typeof modal.key === 'string' ? modal.key.startsWith(keyPrefix) : false))
      ?.map((modal) => modal.modal) || []
  );
}
